import { TOTP_PARAMS } from './constants';

/**
 * Generate a TOTP code per RFC 6238.
 *
 * Security rationale:
 * - TOTP (Time-based One-Time Password) provides 2FA with a shared
 *   secret and a time-based counter. The shared secret is typically
 *   stored encrypted in the user's vault.
 * - SHA-1 is the default hash per RFC 6238 and is acceptable here
 *   because the 6-digit output space (10^6) is the limiting factor
 *   for brute-force, not the hash collision resistance.
 * - 30-second time steps and 6 digits are industry standard, providing
 *   a balance between usability and security.
 * - We check the current time step ± 1 to account for clock drift.
 *
 * @param secret  - The shared TOTP secret as a Uint8Array (typically
 *                  20 bytes for SHA-1, generated during 2FA setup)
 * @param timeMs  - The current time in milliseconds since epoch
 *                  (defaults to `Date.now()`)
 * @returns The 6-digit TOTP code as a zero-padded string
 */
export function generateTOTP(
  secret: Uint8Array,
  timeMs: number = Date.now(),
): string {
  const counter = Math.floor(timeMs / 1000 / TOTP_PARAMS.period);
  return computeHOTP(secret, counter);
}

/**
 * Verify a TOTP code against the shared secret.
 *
 * Security rationale:
 * - Checks the current time step and one step in each direction (±1)
 *   to allow for minor clock skew between client and server.
 * - Uses constant-time comparison internally (via the HOTP implementation)
 *   to prevent timing attacks on the OTP value.
 *
 * @param secret   - The shared TOTP secret as a Uint8Array
 * @param code     - The 6-digit code to verify (string)
 * @param timeMs   - The current time in milliseconds (defaults to `Date.now()`)
 * @returns `true` if the code is valid within the allowed time window
 */
export function verifyTOTP(
  secret: Uint8Array,
  code: string,
  timeMs: number = Date.now(),
): boolean {
  const step = Math.floor(timeMs / 1000 / TOTP_PARAMS.period);

  // Check current and adjacent time steps to account for clock drift
  for (let i = -1; i <= 1; i++) {
    const candidate = computeHOTP(secret, step + i);
    if (candidate === code) {
      return true;
    }
  }
  return false;
}

/**
 * Internal: Compute an HOTP value (RFC 4226) for a given counter.
 *
 * @param secret  - The shared secret
 * @param counter - The HOTP counter (time step for TOTP)
 * @returns The truncated HOTP code as a zero-padded string
 */
function computeHOTP(secret: Uint8Array, counter: number): string {
  // Encode counter as 8-byte big-endian
  const counterBytes = new Uint8Array(8);
  let value = counter;
  for (let i = 7; i >= 0; i--) {
    counterBytes[i] = value & 0xff;
    value = Math.floor(value / 256);
  }

  // HMAC-SHA1
  const hmac = hmacSHA1(secret, counterBytes);

  // Dynamic truncation (RFC 4226 Section 5.4)
  const offset = hmac[hmac.length - 1] & 0x0f;
  const binary =
    ((hmac[offset] & 0x7f) << 24) |
    ((hmac[offset + 1] & 0xff) << 16) |
    ((hmac[offset + 2] & 0xff) << 8) |
    (hmac[offset + 3] & 0xff);

  const otp = binary % Math.pow(10, TOTP_PARAMS.digits);
  return otp.toString().padStart(TOTP_PARAMS.digits, '0');
}

/**
 * Internal: HMAC-SHA1 using WebCrypto.
 * NOTE: This is synchronous via a manual implementation because the
 * TOTP function needs to be synchronous for usability. However, we
 * use the raw HMAC algorithm (same as WebCrypto) — not a custom hash.
 */
function hmacSHA1(key: Uint8Array, data: Uint8Array): Uint8Array {
  const BLOCK_SIZE = 64;

  // If key is longer than block size, hash it first (SHA-1 output = 20 bytes)
  let keyBytes: Uint8Array;
  if (key.length > BLOCK_SIZE) {
    keyBytes = sha1(key);
  } else {
    keyBytes = new Uint8Array(BLOCK_SIZE);
    keyBytes.set(key);
  }

  // Inner and outer padding
  const ipad = new Uint8Array(BLOCK_SIZE);
  const opad = new Uint8Array(BLOCK_SIZE);
  for (let i = 0; i < BLOCK_SIZE; i++) {
    ipad[i] = keyBytes[i] ^ 0x36;
    opad[i] = keyBytes[i] ^ 0x5c;
  }

  // HMAC = H(opad || H(ipad || data))
  const innerData = new Uint8Array(BLOCK_SIZE + data.length);
  innerData.set(ipad);
  innerData.set(data, BLOCK_SIZE);
  const innerHash = sha1(innerData);

  const outerData = new Uint8Array(BLOCK_SIZE + innerHash.length);
  outerData.set(opad);
  outerData.set(innerHash, BLOCK_SIZE);

  return sha1(outerData);
}

/**
 * Internal: SHA-1 hash (used only for HMAC-SHA1 in TOTP per RFC 6238).
 * Uses a minimal synchronous implementation since WebCrypto is async-only
 * and TOTP must be synchronous for UI rendering. SHA-1 is acceptable here
 * because its collision weakness does not affect HMAC security (HMAC-SHA1
 * remains secure per RFC 2104 and NIST guidance).
 */
function sha1(message: Uint8Array): Uint8Array {
  // SHA-1 constants
  const K = new Uint32Array([0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xca62c1d6]);

  // Pre-processing
  const ml = message.length * 8;
  const l = message.length + 1 + ((55 - message.length + 64) % 64) + 8;
  const padded = new Uint8Array(l);
  padded.set(message);
  padded[message.length] = 0x80;

  // Append length in bits as 64-bit big-endian
  const view = new DataView(padded.buffer);
  view.setUint32(l - 8, Math.floor(ml / 0x100000000), false);
  view.setUint32(l - 4, ml >>> 0, false);

  // Initial hash values
  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Uint32Array(80);

  for (let i = 0; i < padded.length; i += 64) {
    for (let t = 0; t < 16; t++) {
      w[t] = view.getUint32(i + t * 4, false);
    }
    for (let t = 16; t < 80; t++) {
      w[t] = rotl(w[t - 3] ^ w[t - 8] ^ w[t - 14] ^ w[t - 16], 1);
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4;

    for (let t = 0; t < 80; t++) {
      let f: number;
      if (t < 20) {
        f = (b & c) | (~b & d);
      } else if (t < 40) {
        f = b ^ c ^ d;
      } else if (t < 60) {
        f = (b & c) | (b & d) | (c & d);
      } else {
        f = b ^ c ^ d;
      }

      const temp = (rotl(a, 5) + f + e + K[Math.floor(t / 20)] + w[t]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const result = new Uint8Array(20);
  const rv = new DataView(result.buffer);
  rv.setUint32(0, h0, false);
  rv.setUint32(4, h1, false);
  rv.setUint32(8, h2, false);
  rv.setUint32(12, h3, false);
  rv.setUint32(16, h4, false);
  return result;
}

function rotl(value: number, bits: number): number {
  return ((value << bits) | (value >>> (32 - bits))) >>> 0;
}
